/* eslint react/prop-types: 0, react/display-name: 0 */

import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import get from 'lodash.get';

// Components
import BuyerAssuranceIcon from 'dibs-vg/dist/react/buyer-assurance';
import CartItemImage from './CartItemImage';
import CartItemTitle from './CartItemTitle';
import CartItemPricing from './CartItemPricing';
import CartItemCTA from './CartItemCTA';
import ItemFolderWrapper from 'dibs-portfolio-folder/exports/ItemFolderWrapper';
import ItemHeartWrapper from 'dibs-portfolio-heart/exports/ItemHeartWrapper';
import CartRemoveItemLink from './CartRemoveItemLink';
import CartQuantitySelect from './CartQuantitySelect';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

// Helpers
import folderTracking from './cartFolderTracking';
import tracking from 'dibs-tracking';
import { getBuyerId, getEverLoggedIn } from 'dibs-cookie-jar';

// Styles
import styles from './styles/CartItem.scss';

const { SAVE_ITEM_TO_FOLDER_FLOW, SAVE_ITEM_FLOW } = authModalLoader.constants;

function showAuthModal({
    flow,
    onSuccess = () => {},
    onError = () => {},
    shouldPauseAuthFlow = () => {},
} = {}) {
    const { LOGIN, REGISTER } = authModalLoader.constants;
    if (!getBuyerId(document.cookie)) {
        authModalLoader
            .show({
                flow,
                shouldPauseAuthFlow,
                action: getEverLoggedIn(document.cookie) ? LOGIN : REGISTER,
            })
            .then(({ userId }) => {
                if (!userId) {
                    onError();
                } else {
                    onSuccess();
                }
            });
    } else {
        onSuccess();
    }
}
function getFolderModalComponent() {
    return new Promise((resolve, reject) => {
        showAuthModal({
            flow: SAVE_ITEM_TO_FOLDER_FLOW,
            shouldPauseAuthFlow: state => state.step === 0,
            onSuccess: () => {
                return import(
                    /* webpackChunkName: "AddToFolderModalWrapper" */
                    'dibs-portfolio-folder/src/components/AddToFolderModalWrapper'
                )
                    .then(exported => exported.default)
                    .then(resolve);
            },
            onError: reject,
        });
    });
}

const CartItem = props => {
    const { item, onClickProduct, onRemoveItem, user, viewer } = props;
    const userId = get(user, 'serviceId') || '';
    const isInProcurement = get(item, 'isInProcurement') || false;
    const priceDisplays = get(item, 'displayInfo.pricing') || {};
    const isUnavailable =
        item.isDeleted ||
        item.isNotListed ||
        item.seller.isClosed ||
        item.seller.isPending ||
        item.seller.isSuspended;
    const isPurchasable = !priceDisplays.showSold && !isUnavailable;

    return (
        <div data-tn="cart-item" className={styles.wrapper}>
            <div className="rowFlex">
                <div className="colLg3 colMd5 colXs12">
                    <CartItemImage
                        item={item}
                        isUnavailable={isUnavailable}
                        onClickProductImage={onClickProduct}
                    />
                </div>
                <div className="colLg5 colMd7 colXs12">
                    <div className={styles.middle}>
                        <div className={styles.favorites}>
                            <div className={styles.heartWrapper}>
                                <ItemHeartWrapper
                                    authModalShow={cb => {
                                        return showAuthModal({
                                            flow: SAVE_ITEM_FLOW,
                                            onSuccess: cb,
                                        });
                                    }}
                                    item={props.item}
                                    loadPortfolioData={!!userId}
                                    onFavorited={() => {
                                        tracking.trackEvent({
                                            action: 'item added',
                                            label: 'cart',
                                            category: 'item favoriting',
                                            additional: {
                                                itemPk: item.serviceId,
                                            },
                                        });
                                    }}
                                    onUnFavorited={() => {
                                        tracking.trackEvent({
                                            action: 'item removed',
                                            label: 'cart',
                                            category: 'item favoriting',
                                            additional: {
                                                itemPk: item.serviceId,
                                            },
                                        });
                                    }}
                                    viewer={viewer}
                                    userId={userId}
                                    userIds={[userId]}
                                />
                            </div>
                            <div className={styles.folderWrapper}>
                                <ItemFolderWrapper
                                    authModalTrackingLabel="save item - cart page"
                                    selectedItemImages={[item.firstPhotoWebPath]}
                                    selectedItemTitles={[item.title]}
                                    shouldFillInFolder
                                    singlePortfolioItem
                                    trackingHooks={folderTracking}
                                    folderPortfolioItems={item.folderPortfolioItems}
                                    item={item}
                                    userId={userId}
                                    userIds={[userId]}
                                    getModalComponent={getFolderModalComponent}
                                    loadPortfolioData={!!userId}
                                    onAddItemSuccess={() => {
                                        if (authModalLoader.isPaused()) {
                                            authModalLoader.resume();
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <CartItemTitle
                            item={item}
                            user={user}
                            isUnavailable={isUnavailable}
                            onClick={onClickProduct}
                        />
                        {(isPurchasable || priceDisplays.showOnHold) && (
                            <CartItemPricing user={user} item={item} />
                        )}
                        {isInProcurement && (
                            <div className={styles.inProcurement}>
                                <BuyerAssuranceIcon className={styles.inProcurementIcon} />
                                <FormattedMessage
                                    id="CartItem.inProcurement"
                                    defaultMessage="Requested for Purchasing Service"
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className={`colLg4 colXs12 rowFlex ${styles.buttons}`}>
                    <div className="colXs12 colXsCenter">
                        <CartQuantitySelect item={item} selectClassName={styles.quantitySelect}>
                            {quantitySelected => (
                                <CartItemCTA
                                    item={item}
                                    user={user}
                                    isUnavailable={isUnavailable}
                                    isPurchasable={isPurchasable}
                                    quantitySelected={quantitySelected}
                                />
                            )}
                        </CartQuantitySelect>
                    </div>
                </div>
                <CartRemoveItemLink user={user} viewer={viewer} onRemoveItem={onRemoveItem} />
            </div>
        </div>
    );
};

CartItem.propTypes = {
    viewer: PropTypes.object.isRequired,
    item: PropTypes.object,
    onRemoveItem: PropTypes.func,
    onClickProduct: PropTypes.func,
};

export default createFragmentContainer(CartItem, {
    viewer: graphql`
        fragment CartItem_viewer on Viewer {
            ...ItemHeartWrapper_viewer
                @arguments(userIds: $userIds, loadPortfolioData: $loadPortfolioData)
        }
    `,
    user: graphql`
        fragment CartItem_user on User {
            serviceId
            ...CartItemPricing_user
            ...CartItemTitle_user
        }
    `,
    item: graphql`
        fragment CartItem_item on Item {
            ...CartItemImage_item
            ...CartItemTitle_item
            ...CartItemPricing_item
            ...CartItemCTA_item
            ...ItemFolderWrapper_item
                @arguments(userId: $userId, loadPortfolioData: $loadPortfolioData)
            ...ItemHeartWrapper_item
                @arguments(userId: $userId, loadPortfolioData: $loadPortfolioData)
            ...CartQuantitySelect_item
            serviceId
            isNotListed
            isDeleted
            isInProcurement(userId: $userId) @include(if: $hasUserId)
            seller {
                isClosed
                isPending
                isSuspended
            }
            firstPhotoWebPath(size: large)
            title
            displayInfo(buyerId: $userId, page: pdp) {
                pricing {
                    showOnHold
                    showSold
                }
            }
        }
    `,
});
